import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { Spinner } from '@/components/atoms/Spinner';
import { useCage } from '@/contexts/cage';
import { isBwBioscopeVersion, isColorBioscopeVersion } from '@/utils/bioscopeVersionHelpers';

import {
    getPenDashboardPath,
    getPenHealthPath,
    getWelfareColorRedirectPath,
} from '../Health/_utils/welfareToHealthRedirects';

interface WelfareBioscopeGateProps {
    children: ReactNode;
}

/**
 * Legacy welfare pages replaced by Health on color bioscopes (2.0 / 3.0).
 * Does not wrap /welfare/speed — fish speed is separate from welfare and health.
 * Pens without a known bioscope version redirect to the cage dashboard.
 */
export function WelfareBioscopeGate(props: WelfareBioscopeGateProps) {
    const { children } = props;
    const location = useLocation();
    const { loading, bioscope } = useCage();
    const bioscopeVersion = bioscope?.version;

    if (loading) {
        return <Spinner size="lg" fullScreen />;
    }

    if (isColorBioscopeVersion(bioscopeVersion)) {
        const redirectTo =
            getWelfareColorRedirectPath(location.pathname) ?? getPenHealthPath(location.pathname);
        return redirectTo ? <Navigate to={redirectTo} replace /> : null;
    }

    if (!isBwBioscopeVersion(bioscopeVersion)) {
        const redirectTo = getPenDashboardPath(location.pathname);
        return redirectTo ? <Navigate to={redirectTo} replace /> : null;
    }

    return children;
}
